import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const Styles = () => (
    <style>{`
        :root {
            --success-green: #22c55e;
            --dark-text: #1f2937;
            --light-text: #6b7280;
            --background-light: #f9fafb;
            --card-background: #ffffff;
            --border-color: #e5e7eb;
        }

        .success-page-container {
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            background: linear-gradient(135deg, #f0fdf4 0%, #eff6ff 100%);
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            padding: 1rem;
        }

        .success-card {
            background-color: var(--card-background);
            border-radius: 16px;
            padding: 2.5rem 3rem;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
            text-align: center;
            max-width: 500px;
            width: 100%;
            border: 1px solid var(--border-color);
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
        }

        .checkmark-container {
            width: 100px;
            height: 100px;
            margin-bottom: 1rem;
        }

        .checkmark-svg {
            width: 100px;
            height: 100px;
            border-radius: 50%;
            display: block;
        }
        
        .success-title {
            font-size: 2rem;
            font-weight: 700;
            color: var(--dark-text);
            margin: 0;
        }

        .success-subtitle {
            font-size: 1rem;
            color: var(--light-text);
            max-width: 400px;
            line-height: 1.6;
            margin: 0;
        }

        .verification-notice {
            background-color: var(--background-light);
            border: 1px solid var(--border-color);
            border-radius: 8px;
            padding: 0.75rem 1.25rem;
            font-size: 0.9rem;
            color: var(--dark-text);
            margin-top: 0.5rem;
        }

        .verification-notice strong {
            color: var(--success-green);
            font-weight: 600;
        }

        .login-button {
            background-color: var(--success-green);
            color: white;
            font-size: 1rem;
            font-weight: 600;
            border: none;
            border-radius: 8px;
            padding: 0.8rem 2rem;
            cursor: pointer;
            transition: transform 0.2s ease, box-shadow 0.2s ease;
            margin-top: 1.5rem;
        }

        .login-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(34, 197, 94, 0.2);
        }
    `}</style>
);

const AnimatedCheckmark = () => {
    const checkVariants = {
        hidden: { pathLength: 0 },
        visible: { 
            pathLength: 1,
            transition: { duration: 0.4, ease: "easeOut", delay: 0.5 }
        }
    };

    const circleVariants = {
        hidden: { strokeDashoffset: 166 },
        visible: { 
            strokeDashoffset: 0,
            transition: { duration: 0.6, ease: [0.65, 0, 0.45, 1], delay: 0.2 }
        }
    };

    return (
        <motion.div
            className="checkmark-container"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.1 }}
        >
            <svg className="checkmark-svg" viewBox="0 0 52 52">
                <motion.circle
                    cx="26" cy="26" r="25"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="3"
                    strokeDasharray="166"
                    variants={circleVariants}
                    initial="hidden"
                    animate="visible"
                />
                <motion.path
                    d="M14.1 27.2l7.1 7.2 16.7-16.8"
                    fill="none"
                    stroke="var(--success-green)"
                    strokeWidth="3"
                    variants={checkVariants}
                    initial="hidden"
                    animate="visible"
                />
            </svg>
        </motion.div>
    );
};

export default function RegistrationSuccess() {
    const navigate = useNavigate()
    const handleClick = () => {
        navigate('/vendor-login')
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2, delayChildren: 0.8 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1, y: 0,
            transition: { type: "spring", stiffness: 100 }
        }
    };

    return (
        <div className="success-page-container">
            <Styles />
            <motion.div
                className="success-card"
                initial="hidden"
                animate="visible"
                variants={containerVariants}
            >
                <AnimatedCheckmark />
                
                <motion.h1 variants={itemVariants} className="success-title">
                    Registration Successful!
                </motion.h1>
                
                <motion.p variants={itemVariants} className="success-subtitle">
                    Your profile has been submitted and is now under verification.
                </motion.p>
                
                <motion.div variants={itemVariants} className="verification-notice">
                    You'll receive an email about your account status within <strong>2–4 working days</strong>.
                </motion.div>
                
                <motion.div variants={itemVariants}>
                    <button className="login-button" onClick={handleClick}>
                        Continue to Login
                    </button>
                </motion.div>
            </motion.div>
        </div>
    );
}

